import { useRef, useState, useCallback, useEffect } from 'react';

export interface SwipeState {
  ref: React.RefObject<HTMLDivElement | null>;
  swiping: boolean;
  direction: 'left' | 'right' | null;
  offset: number;
}

interface PointerStart {
  x: number;
  y: number;
  time: number;
}

const SWIPE_THRESHOLD = 80;

function isFinePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(pointer: fine)').matches;
}

function shouldIgnoreSwipeTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return !!target.closest(
    'button, a, input, textarea, select, [contenteditable="true"], [data-no-swipe]',
  );
}

export function useSwipe(onSwipeRight?: () => void, onSwipeLeft?: () => void): SwipeState {
  const ref = useRef<HTMLDivElement | null>(null);
  const [swiping, setSwiping] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const [offset, setOffset] = useState(0);

  const startRef = useRef<PointerStart | null>(null);
  const cancelledRef = useRef(false);

  const handlePointerDown = useCallback((e: PointerEvent) => {
    if (isFinePointer() || shouldIgnoreSwipeTarget(e.target)) {
      startRef.current = null;
      cancelledRef.current = true;
      setSwiping(false);
      setDirection(null);
      setOffset(0);
      return;
    }

    startRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    cancelledRef.current = false;
    setSwiping(false);
    setDirection(null);
    setOffset(0);
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!startRef.current || cancelledRef.current) return;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;

    // Cancel if vertical movement exceeds horizontal (user is scrolling)
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      cancelledRef.current = true;
      setSwiping(false);
      setDirection(null);
      setOffset(0);
      return;
    }

    if (Math.abs(dx) > 10) {
      setSwiping(true);
      setDirection(dx > 0 ? 'right' : 'left');
      setOffset(dx);
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!startRef.current || cancelledRef.current) {
      startRef.current = null;
      return;
    }

    const currentOffset = offset;

    if (currentOffset >= SWIPE_THRESHOLD && onSwipeRight) {
      onSwipeRight();
    } else if (currentOffset <= -SWIPE_THRESHOLD && onSwipeLeft) {
      onSwipeLeft();
    }

    startRef.current = null;
    setSwiping(false);
    setDirection(null);
    setOffset(0);
  }, [offset, onSwipeRight, onSwipeLeft]);

  const handlePointerCancel = useCallback(() => {
    startRef.current = null;
    cancelledRef.current = true;
    setSwiping(false);
    setDirection(null);
    setOffset(0);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.addEventListener('pointerdown', handlePointerDown);
    el.addEventListener('pointermove', handlePointerMove);
    el.addEventListener('pointerup', handlePointerUp);
    el.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      el.removeEventListener('pointerdown', handlePointerDown);
      el.removeEventListener('pointermove', handlePointerMove);
      el.removeEventListener('pointerup', handlePointerUp);
      el.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel]);

  return { ref, swiping, direction, offset };
}
